/**
 * MER-245: bind every customer-data query to a tenant_id at the
 * application layer. RLS (migration 0016 effective_tenant_filter) is
 * defense in depth, NOT the only gate: staff sessions that are not
 * impersonating match every row (case b), so an unscoped query run by a
 * staff user blends all tenants and the largest tenant's data dominates
 * the aggregate. Binding tenant_id to the URL slug fixes that at the
 * source.
 *
 * Pure and dependency-free so node:test can import it directly with no
 * TypeScript build step (the dashboard ships TS via Vite; node --test
 * runs raw .mjs). The companion tenantScope.d.mts gives TS callers types.
 *
 * Fail-closed: a falsy tenantId throws rather than building an unscoped
 * query. Call sites gate on a resolved tenant_id (useTenantId) before
 * reaching here, so the throw is a backstop, not the normal path.
 */

export const TENANT_COLUMN = "tenant_id";

/**
 * Append `.eq("tenant_id", tenantId)` to a PostgREST filter builder.
 *
 * @param {{ eq: (column: string, value: unknown) => unknown }} query
 *   A Supabase/PostgREST builder returned by `.from(table).select(...)`.
 * @param {string} tenantId  The resolved tenant UUID. Must be non-empty.
 * @returns the same builder, scoped to the tenant.
 */
export function scopeTenant(query, tenantId) {
  if (typeof tenantId !== "string" || tenantId.length === 0) {
    throw new Error(
      "scopeTenant: refusing to build an unscoped customer-data query. " +
        `tenantId must be a non-empty string, got ${describe(tenantId)}. ` +
        "RLS is defense in depth, not the only tenant gate (staff sessions " +
        "match all rows); the query must bind tenant_id explicitly.",
    );
  }
  if (!query || typeof query.eq !== "function") {
    throw new Error(
      "scopeTenant: query is not a PostgREST filter builder (no .eq method). " +
        "Call scopeTenant on the result of .from(table).select(...).",
    );
  }
  return query.eq(TENANT_COLUMN, tenantId);
}

function describe(v) {
  if (v === null) return "null";
  if (v === undefined) return "undefined";
  if (v === "") return "empty string";
  return `${typeof v} ${JSON.stringify(v)}`;
}

/**
 * Whether a relation can be bound by tenant_id at the application layer.
 *
 * MER-500: every relation, including the public.view_cqt_* views, now
 * projects tenant_id. The view translator
 * (src/meridian_pipeline/supabase/views.py) adds tenant_id to every
 * generated view's SELECT + GROUP BY (migration 0043), closing the
 * MER-245 residual where a non-impersonating staff session blended
 * tenants inside a view's server-side GROUP BY. The old view_cqt_
 * exemption is gone: views bind exactly like base tables.
 */
export function isTenantScopableTable(table) {
  return typeof table === "string" && table.length > 0;
}

/**
 * Bind tenant_id for every relation (base canonical tables AND CQT
 * views, which project tenant_id as of migration 0043 / MER-500). Kept
 * as the single chokepoint so the unscoped-query lint and any future
 * relation-specific handling stay in one place.
 *
 * @param {{ eq: (column: string, value: unknown) => unknown }} query
 * @param {string} tenantId
 * @param {string} table  The resolved relation name (`.from(table)`).
 */
export function scopeTenantTable(query, tenantId, table) {
  if (!isTenantScopableTable(table)) return query;
  return scopeTenant(query, tenantId);
}
